import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Learning WebRTC peer-to-peer communication, part 2 – connecting 2 browsers on different devices",
  "description": "",
  "published": "2018-05-16T00:00:00.000Z",
  "redirect_from": ["/blog/learning-webrtc-peer-peer-communication-connecting-browsers-different-devices/swizec/8383"],
  "categories": "Front End, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><em parentName="p">{`This is a Livecoding Recap – an almost-weekly post about interesting things discovered while livecoding. Usually shorter than 500 words. Often with pictures. Livecoding happens almost `}<strong parentName="em">{`every Sunday at 2pm PDT`}</strong>{` on multiple channels. You should subscribe to ￼`}<a parentName="em" {...{
          "href": "https://www.youtube.com/user/TheSwizec"
        }}>{`my Youtube￼ channel`}</a>{` to catch me live.`}</em></p>
    <p>{`It worked! We got two browsers on different machines talking to each other without a server.`}</p>
    <p>{`Well… after the initial handshake. You still need a server for that.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`OMG IT WORKS! I got two browsers to talk to each other without a server`}<br parentName="p"></br><br parentName="p"></br>{`Well ... a server is used for the initial handshake. BUT THEN NO SERVER `}<a parentName="p" {...{
          "href": "https://twitter.com/hashtag/WebRTC?src=hash&ref_src=twsrc%5Etfw"
        }}>{`#WebRTC`}</a><br parentName="p"></br><br parentName="p"></br>{`Article coming soon `}<a parentName="p" {...{
          "href": "https://t.co/LRWVt4BhV8"
        }}>{`pic.twitter.com/LRWVt4BhV8`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/995933206187728896"
      }}>{`May 14, 2018`}</a></blockquote>
    <p>{`That was a fun weekend. Two livecoding sessions. Finally got it working late at night when the cameras weren't rolling. I am nowhere near having it working in time for a true blockchain demo for my WeAreDevelopers talk this Friday. 😅`}</p>
    <lite-youtube {...{
      "videoid": "8TFj_U5CyWQ",
      "videostartat": "0"
    }}></lite-youtube>
    <lite-youtube {...{
      "videoid": "L8bsztuOEeQ",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`You can `}<a parentName="p" {...{
        "href": "https://swizec.github.io/webrtc-sample/"
      }}>{`try it out here`}</a>{`. Open that link in two browsers, possibly on different machines.`}</p>
    <p>{`You might have to try a couple of times, it's a little finicky. 🤨`}</p>
    <p>{`Here's how it works 👇`}</p>
    <h2 {...{
      "id": "how-to-use-webrtc-to-connect-browsers-on-different-devices"
    }}>{`How to use WebRTC to connect browsers on different devices`}</h2>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/05/photo_may_16_1_09_35_pm.jpg",
        "alt": null
      }}></img></p>
    <p>{`You can use WebRTC to make browsers talk to each other directly without a server. But because there's no service discovery, you need a signaling server so browsers can find each other.`}</p>
    <p>{`The flow goes like this:`}</p>
    <ol>
      <li parentName="ol">{`Client 1 says hi to the server and registers`}</li>
      <li parentName="ol">{`Client 2 says hi to the server and registers`}</li>
      <li parentName="ol">{`Server holds list of identifiers (usernames)`}</li>
      <li parentName="ol">{`Client 1 tells server to call Client 2`}</li>
      <li parentName="ol">{`Server tells Client 2 there's a call`}</li>
      <li parentName="ol">{`Client 2 answers the call`}</li>
      <li parentName="ol">{`Client 1 and Client 2 are now talking directly`}</li>
    </ol>
    <p>{`We followed `}<a parentName="p" {...{
        "href": "https://github.com/mdn/samples-server/tree/master/s/webrtc-from-chat"
      }}>{`this WebRTC chat example from MDN`}</a>{` to model our code.`}</p>
    <h3 {...{
      "id": "signaling-websocket-server"
    }}>{`Signaling WebSocket server`}</h3>
    <p>{`Signaling is the handshake process between two browsers. Our implementation uses WebSockets to do that.`}</p>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/webrtc-sample/blob/master/signaling-server.js"
      }}>{`server part is the same`}</a>{` as the MDN example. Pure copy pasta.`}</p>
    <p>{`We made some changes to make it work with `}<a parentName="p" {...{
        "href": "https://zeit.co"
      }}>{`now.sh`}</a>{`. Namely, we removed all SSL stuff. Zeit wraps our servers in a secure SSL server that then talks to our actual server via an unencrypted connection.`}</p>
    <p>{`That was a painful gotcha to learn.`}</p>
    <p>{`WebSockets don't work in modern browsers without SSL. And they don't work with self-signed certificates unless you're running localhost. If you want browsers that are not on your machine to talk, you're gonna have to ensure a real SSL cert.`}</p>
    <p>{`Easiest way is to deploy on `}<inlineCode parentName="p">{`now`}</inlineCode>{`.`}</p>
    <h3 {...{
      "id": "connecting-to-the-signaling-server"
    }}>{`Connecting to the signaling server`}</h3>
    <p>{`Talking to the server happens via WebSockets with `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/webrtc-sample/blob/master/src/SignalingConnection.js"
      }}>{`a 40 line helper class`}</a>{`. Instantiate the class, make a connection, listen for messages.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/05/signalconnection.png",
        "alt": null
      }}></img></p>
    <p>{`We create a `}<inlineCode parentName="p">{`new WebSocket`}</inlineCode>{` in `}<inlineCode parentName="p">{`connectToSocket`}</inlineCode>{`, add some callbacks, and hope for the best. The `}<inlineCode parentName="p">{`onmessage`}</inlineCode>{` listener allows us to add additional message listeners later via the `}<inlineCode parentName="p">{`messageListeners`}</inlineCode>{` array.`}</p>
    <p><inlineCode parentName="p">{`sendToServer`}</inlineCode>{` lets us send a JSON object to our server, and `}<inlineCode parentName="p">{`addMsgListener`}</inlineCode>{` lets us add a new message listener. We'll use this to wire up our `}<inlineCode parentName="p">{`PeerConnection`}</inlineCode>{` helper to our server.`}</p>
    <h3 {...{
      "id": "establishing-a-peer-connection"
    }}>{`Establishing a peer connection`}</h3>
    <p>{`Learning our lesson from `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/learning-webrtc-peer-peer-communication-part-1/swizec/8359"
      }}>{`WebRTC part 1`}</a>{`, we split our RTCPeerConnection stuff into `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/webrtc-sample/blob/master/src/PeerConnection.js"
      }}>{`a helper class`}</a>{`.`}</p>
    <p>{`It's about 148 lines and handles the whole lifecycle. We `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/learning-webrtc-peer-peer-communication-part-1/swizec/8359"
      }}>{`talked about the code before`}</a>{`, so here's a recap 👇`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/05/peerconnection.png",
        "alt": null
      }}></img></p>
    <p><inlineCode parentName="p">{`constructor`}</inlineCode>{` sets a bunch of instance vars, sets up a new `}<inlineCode parentName="p">{`RTCPeerConnection`}</inlineCode>{` object, tells it which `}<inlineCode parentName="p">{`iceServers`}</inlineCode>{` to use, connects local event listeners, starts listening for messages on our signaling server, and adds our media stream to the `}<inlineCode parentName="p">{`peerConnection`}</inlineCode>{`.`}</p>
    <p>{`The next step is `}<inlineCode parentName="p">{`handleICECandidate`}</inlineCode>{`, interactive connectivity establishment, which triggers when a new connection is attempted. It pings our signaling server and says `}<em parentName="p">{`"Yo, new ice candidate here"`}</em>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`handleICECandidateEvent = (event) => {
  if (event.candidate) {
    this.signalingConnection.sendToServer({
      type: "new-ice-candidate",
      target: this.targetUsername,
      candidate: event.candidate,
    });
  }
};
`}</code></pre>
    <p>{`After that, we’ve got the `}<inlineCode parentName="p">{`handleNegotiationNeededEvent`}</inlineCode>{`, which is called when `}<inlineCode parentName="p">{`RTCPeerConnection`}</inlineCode>{` says some negotiation needs to happen. I don't know what makes it say that.`}</p>
    <p>{`But the function creates a new connection offer, updates the local SDP description, and tells our signaling server that we're trying to call someone.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`handleNegotiationNeededEvent = () => {
  const { username, targetUsername } = this;
  this.peerConnection
    .createOffer()
    .then((offer) => this.peerConnection.setLocalDescription(offer))
    .then(() =>
      this.signalingConnection.sendToServer({
        name: username,
        target: targetUsername,
        type: "video-offer",
        sdp: this.peerConnection.localDescription,
      })
    )
    .catch(console.error);
};
`}</code></pre>
    <h4 {...{
      "id": "handling-signaling-messages"
    }}>{`Handling signaling messages`}</h4>
    <p>{`Then we have the fun stuff: handling messages from our signaling server.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`onSignalingMessage = (msg) => {
  switch (msg.type) {
    case "video-answer": // Callee has answered our offer
      this.videoAnswer(msg);
      break;

    case "new-ice-candidate": // A new ICE candidate has been received
      this.newICECandidate(msg);
      break;

    case "hang-up": // The other peer has hung up the call
      this.close();
      break;
  }
};
`}</code></pre>
    <p>{`When a message comes in, we can do a couple different things. Set ourselves up as the answer party, add a new candidate to our connection, or close.`}</p>
    <p>{`Those functions are thin wrappers over WebRTC APIs.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`videoAnswer = ({ sdp }) => {
  this.peerConnection
    .setRemoteDescription(new RTCSessionDescription(sdp))
    .catch(console.error);
};

newICECandidate = ({ candidate }) => {
  this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
};

close = () => {
  this.peerConnection.close();
  this.peerConnection = null;

  this.onClose();
};
`}</code></pre>
    <p>{`That's our PeerConnection object. In theory, we could instantiate many of them to connect to multiple remote machines at the same time.`}</p>
    <p>{`That will be a fun experiment.`}</p>
    <p><img parentName="p" {...{
        "src": "/40b62c52945a0ae5fdf69e5b060225df/media-3o7TKT0XDElcmlCeNa-giphy.gif",
        "alt": null
      }}></img></p>
    <h3 {...{
      "id": "putting-it-all-together"
    }}>{`Putting it all together`}</h3>
    <p>{`Holding it all together is our `}<inlineCode parentName="p">{`WebRTCPeerConnectionWithServer`}</inlineCode>{` React component. It renders the UI, instantiates both helper classes from above, and handles the user clicking on buttons to drive the process.`}</p>
    <p>{`You can see `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/webrtc-sample/blob/master/src/WebRTCPeerConnectionWithServer.js"
      }}>{`the whole file on GitHub`}</a>{`.`}</p>
    <p>{`Here are the salient parts. 👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`call = (user) => {
  this.setState({
    targetUsername: user,
  });
  this.createPeerConnection();
};

hangUp = () => {
  this.signalingConnection.sendToServer({
    name: this.state.username,
    target: this.state.targetUsername,
    type: "hang-up",
  });
  this.peerConnection.close();
};

createPeerConnection = () => {
  if (this.peerConnection) return;

  this.peerConnection = new PeerConnection({
    gotRemoteStream: this.gotRemoteStream,
    gotRemoteTrack: this.gotRemoteTrack,
    signalingConnection: this.signalingConnection,
    onClose: this.closeVideoCall,
    localStream: this.state.localStream,
    username: this.state.username,
    targetUsername: this.state.targetUsername,
  });
};

closeVideoCall = () => {
  this.remoteVideoRef.current.srcObject &&
    this.remoteVideoRef.current.srcObject
      .getTracks()
      .forEach((track) => track.stop());
  this.remoteVideoRef.current.src = null;

  this.setState({
    targetUsername: null,
    callDisabled: false,
  });
};
`}</code></pre>
    <p><inlineCode parentName="p">{`call`}</inlineCode>{` is where the fun starts. Saves whom we're calling to state and creates a peer connection.`}</p>
    <p><inlineCode parentName="p">{`createPeerConnection`}</inlineCode>{` passes all the things into our `}<inlineCode parentName="p">{`PeerConnection`}</inlineCode>{` class.`}</p>
    <p><inlineCode parentName="p">{`hangUp`}</inlineCode>{` and `}<inlineCode parentName="p">{`closeVideoCall`}</inlineCode>{` work together to finish our call. We need both because one is user-driven and the other is called when hangup comes from the other side.`}</p>
    <h3 {...{
      "id": "one-last-thing"
    }}>{`One last thing`}</h3>
    <p>{`There's one message from the signaling server we have to handle in the glue area: An offer for a call.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`    case "video-offer": // Invitation and offer to chat
        this.createPeerConnection();
        this.peerConnection.videoOffer(msg);
        break;
`}</code></pre>
    <p>{`When the server tells us someone wants to connect, we have to create a new `}<inlineCode parentName="p">{`PeerConnection`}</inlineCode>{` object on our client and handle the offer. Handling the offer means setting a remote SDP description and sending an answer.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`videoOffer = ({ sdp }) => {
  const { username, targetUsername } = this;

  this.peerConnection
    .setRemoteDescription(new RTCSessionDescription(sdp))
    .then(() => this.peerConnection.createAnswer())
    .then((answer) => {
      return this.peerConnection.setLocalDescription(answer);
    })
    .then(() => {
      this.signalingConnection.sendToServer({
        name: username,
        targetUsername: targetUsername,
        type: "video-answer",
        sdp: this.peerConnection.localDescription,
      });
    })
    .catch(console.error);
};
`}</code></pre>
    <p>{`👌`}</p>
    <h2 {...{
      "id": "and-then-it-works-🤞"
    }}>{`And then it works 🤞`}</h2>
    <p>{`If all the stars align, you can now have a call between 2 browsers on different machines without talking to the server again.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`OMG IT WORKS! I got two browsers to talk to each other without a server`}<br parentName="p"></br><br parentName="p"></br>{`Well ... a server is used for the initial handshake. BUT THEN NO SERVER `}<a parentName="p" {...{
          "href": "https://twitter.com/hashtag/WebRTC?src=hash&ref_src=twsrc%5Etfw"
        }}>{`#WebRTC`}</a><br parentName="p"></br><br parentName="p"></br>{`Article coming soon `}<a parentName="p" {...{
          "href": "https://t.co/LRWVt4BhV8"
        }}>{`pic.twitter.com/LRWVt4BhV8`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/995933206187728896"
      }}>{`May 14, 2018`}</a></blockquote>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      